import type OlMap from "ol/Map";
import { unByKey } from "ol/Observable";
import type { EventsKey } from "ol/events";
import { getPointResolution } from "ol/proj";
import { useCallback, useEffect, useState } from "react";

import { MapControl, useNgwMapContext } from "@nextgis/react-ngw-map";

import { useTranslation } from "../i18n";

import { AttributionControl } from "./map-controls/AttributionControl";
import { ScaleLineControl } from "./map-controls/ScaleLineControl";

import "./MapAttributionBar.css";

const DPI = 96;
const INCHES_PER_METER = 39.37;

function formatScale(value: number, locale: string): string {
  return new Intl.NumberFormat(locale).format(value);
}

function getScale(olMap: OlMap): number | null {
  const view = olMap.getView();
  const resolution = view.getResolution();
  const projection = view.getProjection();
  const center = view.getCenter();

  if (!resolution || !projection) return null;

  const metersPerPixel = getPointResolution(
    projection,
    resolution,
    center || [0, 0],
    "m",
  );

  return Math.round(metersPerPixel * DPI * INCHES_PER_METER);
}

function MapLoadingIndicatorControl({ order }: { order: number }) {
  const { ngwMap } = useNgwMapContext();
  const olMap = ngwMap.mapAdapter?.map as OlMap | undefined;
  const [loadingCount, setLoadingCount] = useState(0);

  useEffect(() => {
    if (!olMap) return;

    const onLoadStart = () => {
      setLoadingCount((value) => value + 1);
    };
    const onLoadEnd = () => {
      setLoadingCount((value) => Math.max(0, value - 1));
    };

    olMap.on("loadstart", onLoadStart);
    olMap.on("loadend", onLoadEnd);

    return () => {
      olMap.un("loadstart", onLoadStart);
      olMap.un("loadend", onLoadEnd);
    };
  }, [olMap]);

  return (
    <MapControl className="map-loading-control" order={order}>
      <div className="map-loading-slot" aria-hidden="true">
        {loadingCount > 0 && <span className="map-loading-spinner" />}
      </div>
    </MapControl>
  );
}

function ScaleInfoControl({ order }: { order: number }) {
  const { locale } = useTranslation();
  const { ngwMap } = useNgwMapContext();
  const olMap = ngwMap.mapAdapter?.map as OlMap | undefined;
  const [scale, setScale] = useState<number | null>(null);

  const updateScale = useCallback(() => {
    if (!olMap) return;
    setScale(getScale(olMap));
  }, [olMap]);

  useEffect(() => {
    if (!olMap) return;

    updateScale();

    const view = olMap.getView();
    const keys: EventsKey[] = [
      view.on("change:resolution", updateScale),
      view.on("change:center", updateScale),
      olMap.on("moveend", updateScale),
    ].flat();

    return () => {
      unByKey(keys);
    };
  }, [olMap, updateScale]);

  return (
    <MapControl className="map-scale-info-control" order={order}>
      <div className="map-scale-info">
        {scale ? `1 : ${formatScale(scale, locale)}` : ""}
      </div>
    </MapControl>
  );
}

export function MapAttributionBar() {
  return (
    <MapControl
      id="map-attribution-toolbar"
      className="map-attribution-toolbar"
      position="bottom-right"
      order={0}
      orientation="horizontal"
      align="center"
      gap={8}
    >
      <MapLoadingIndicatorControl order={10} />
      <AttributionControl order={11} />
      <ScaleLineControl order={20} scaleOptions={{ minWidth: 48 }} />
      <ScaleInfoControl order={40} />
    </MapControl>
  );
}
